<script setup>
import { useBranding } from '@/branding';
import UiAuthBrandPanel from './UiAuthBrandPanel.vue';

// Split auth layout from the Laravel starter kit: the school's brand panel
// on the left and the form centered on the right. Below 960px only the
// form shows, with the logo above it. "Back to home" (top left) returns to
// the public home page.
defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
});

const branding = useBranding();
</script>

<template>
  <div class="auth">
    <UiAuthBrandPanel class="auth__side" />

    <main class="auth__main">
      <router-link to="/" class="auth__back">
        <v-icon size="16">mdi-arrow-left</v-icon>
        Back to home
      </router-link>

      <div class="auth__panel">
        <router-link to="/" class="auth__mobile-logo">
          <img :src="branding.logoUrl" :alt="branding.shortName" />
        </router-link>

        <header class="auth__header">
          <h1 class="auth__title">{{ title }}</h1>
          <p v-if="description" class="auth__description">{{ description }}</p>
        </header>

        <slot />

        <footer v-if="$slots.footer" class="auth__footer">
          <slot name="footer" />
        </footer>
      </div>
    </main>
  </div>
</template>

<style scoped>
.auth {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 100vh;
  background: var(--background);
  color: var(--foreground);
}

.auth__main {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 72px 24px 40px;
}
.auth__back {
  position: absolute;
  top: 20px;
  left: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--muted-foreground);
  text-decoration: none;
}
.auth__back:hover,
.auth__back:focus-visible {
  outline: none;
  background: var(--accent);
  color: var(--foreground);
}
.auth__panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 360px;
}
.auth__mobile-logo {
  display: none;
  align-self: center;
}
.auth__mobile-logo img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
.auth__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
}
.auth__title {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.auth__description {
  margin: 0;
  font-size: 14px;
  color: var(--muted-foreground);
}
.auth__footer {
  font-size: 14px;
  text-align: center;
  color: var(--muted-foreground);
}
.auth__footer :deep(a) {
  color: var(--foreground);
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 4px;
}

@media (max-width: 960px) {
  .auth {
    grid-template-columns: 1fr;
  }
  .auth__side {
    display: none;
  }
  .auth__mobile-logo {
    display: block;
  }
}
</style>
